import { useEffect, useRef } from 'react';
import { useAuth } from 'react-oidc-context';
import { setTokenGetter, setAuthActions, apiHeaders, apiUrl } from '../api/client';
import apiClient from '../api/client';
import { API } from '../api/endpoints';
import { useGlobalStore, NO_ROLE } from '../stores/globalStore';
import { useConnectionStore, validateConnectionOwner } from '../stores/connectionStore';
import { getUserManager } from './authConfig';
import { useSessionLifetime } from './useSessionLifetime';
import { useThemeStore } from '../stores/themeStore';
import { useSTDisplayStore } from '../stores/stDisplayStore';
import { activateSharedPrefs, loadSharedPrefs } from '../hooks/sharedPrefs';
import { reapplyUserSettings } from '../hooks/reapplyUserSettings';
import { useFeatureVisibilityStore } from '../stores/featureVisibilityStore';
import { resolveFacilityFromMappings } from './resolveFacilityFromMappings';
import { getRecentFacilityIds, recordRecentFacility } from '../utils/recentFacilities';
import { isApiBlocked, isSignedOutError, usePollingAllowed } from './pollingGate';
import { ssoRefusalMessage } from './ssoRefusal';

/**
 * Bridges react-oidc-context auth into the module-scoped axios interceptor.
 * Calls GET /api/user/Me to resolve the authenticated user's identity server-side
 * (JWT email + X-Connection-Id → tblConnection_User GUID → tblPMSec_User record).
 * No user-controllable lookup key — the API determines identity from the auth context.
 *
 * IMPORTANT: The Me endpoint requires X-Connection-Id header, so we must wait for
 * a connection to be selected before making this call.
 *
 * Renders nothing - pure side-effect component.
 */
export default function AuthTokenSetter() {
  const auth = useAuth();
  const selectedConnection = useConnectionStore((s) => s.selectedConnection);
  const selectionId = useConnectionStore((s) => s.selectionId);
  // Bumped by the Try again button shown when the facility list or role could not be read.
  const retryNonce = useGlobalStore((s) => s.sessionRetryNonce);

  // The access token requests carry. A ref, not a closure over auth.user: a
  // renewal stores the new token here the moment it arrives, so the 401
  // interceptor's retry (which runs before React re-renders with the new user)
  // already sends it. With the closure the retry went out with the old token
  // and failed again.
  const tokenRef = useRef<string | undefined>(auth.user?.access_token);

  // Keep the token getter in sync with the current user's access token
  useEffect(() => {
    tokenRef.current = auth.user?.access_token;
    const getter = () => tokenRef.current;
    setTokenGetter(getter);
    return () => setTokenGetter(() => undefined);
  }, [auth.user]);

  // On shared devices (iPads in kitchens), a different user may log in on
  // the same tab. If the saved connection belongs to a different user, clear
  // it so MainLayout redirects to /connection for a fresh selection.
  useEffect(() => {
    const email = auth.user?.profile?.email || auth.user?.profile?.preferred_username;
    if (email) {
      validateConnectionOwner(email);
    }
  }, [auth.user?.profile?.email, auth.user?.profile?.preferred_username]);

  // Provide auth actions for the shared renewal (401 interceptor + proactive).
  // Renewal goes straight to the UserManager, not auth.signinSilent(): the
  // context wrapper sets auth.isLoading for the duration, and AuthGuard then
  // swaps the whole page for the LoadingScreen (see getUserManager).
  const signinRedirect = auth.signinRedirect;
  useEffect(() => {
    setAuthActions({
      signinSilent: async () => {
        const renewed = await getUserManager().signinSilent();
        if (renewed?.access_token) tokenRef.current = renewed.access_token;
        return renewed;
      },
      signinRedirect: () => signinRedirect(),
    });
    return () => setAuthActions(null);
  }, [signinRedirect]);

  // Renew ahead of expiry while the user works; warn only when the sign-in
  // itself (the refresh token) ends soon, never for the access token.
  useSessionLifetime(auth.user);

  // The connection pick the last COMPLETED resolve was for, as
  // `${connectionId}:${selectionId}`. Null while a resolve is in flight or
  // after one failed, so the next run tries again.
  const resolvedForRef = useRef<string | null>(null);
  // The facility this tab had when its facility list could not be read. That failure clears the
  // facility (fail closed), so Try again found none and opened the user's default: Roberta,
  // working in Hospitality, "all of a sudden" was in Acacia (2026-10-13; a reload around a deploy
  // is the usual way in). Kept with its connection, so it never carries into another database.
  const keptFacilityRef = useRef<{ connectionId: number; facilityId: number } | null>(null);

  // Resolve the authenticated user via GET /api/user/Me.
  // The API determines identity from JWT email + X-Connection-Id (no client-controlled lookup key).
  // Waits for selectedConnection so X-Connection-Id header is available.
  // Re-runs on every connection pick (selectionId), and when auth.user changes.
  useEffect(() => {
    if (!auth.user?.access_token) return;

    // Must wait for a connection to be selected - the API needs X-Connection-Id.
    if (!selectedConnection) return;

    // Keyed on the PICK, not just the connection id. Picking a connection
    // resets the facility to 0 (ConnectionSelection's applyConnection), so a
    // re-pick of the SAME connection must resolve again. Keying on the id alone
    // skipped it, left the facility at 0 and StartFormRedirect spinning forever.
    // A token refresh (auth.user changes, same pick) still skips.
    const resolveKey = `${selectedConnection.connectionId}:${selectionId}:${retryNonce}`;
    const connectionId = selectedConnection.connectionId;
    if (resolvedForRef.current === resolveKey) return;
    resolvedForRef.current = null;

    const email = auth.user?.profile?.email || auth.user?.profile?.preferred_username;

    let cancelled = false;
    const resolveUser = async () => {
      useGlobalStore.getState().setSessionLoadError(null);
      // The facility this tab already had open (sessionStorage), read before anything here
      // changes it. A refresh keeps it; a connection pick has reset it to 0 and a new tab has
      // none, so those get the user's default. resolveFacilityFromMappings only honours it
      // when it is one of the user's own mappings.
      const kept = keptFacilityRef.current;
      const tabFacility =
        useGlobalStore.getState().facilityId ||
        (kept && kept.connectionId === connectionId ? kept.facilityId : 0);
      try {
        const { data } = await apiClient.get(API.userMe);
        if (cancelled || !data) return;

        // Case-insensitive property lookup - System.Text.Json camelCase output
        // varies with consecutive uppercase (PKEY_User → pkeY_User or pkey_User).
        const get = (obj: Record<string, unknown>, ...keys: string[]): unknown => {
          for (const k of keys) {
            if (k in obj) return obj[k];
          }
          // fallback: case-insensitive search
          const lowerKeys = keys.map((k) => k.toLowerCase());
          for (const objKey of Object.keys(obj)) {
            if (lowerKeys.includes(objKey.toLowerCase())) return obj[objKey];
          }
          return undefined;
        };

        const userId = (get(data, 'pkeY_User', 'pkey_User', 'pKEY_User', 'PKEY_User') ?? 0) as number;
        const userName = (get(data, 'userName', 'UserName') ?? '') as string;
        const firstName = (get(data, 'fName', 'FName') ?? '') as string;
        const lastName = (get(data, 'lName', 'LName') ?? '') as string;
        const defaultFacility = (get(data, 'dF_Dietech_Facility', 'df_Dietech_Facility', 'DF_Dietech_Facility') ?? 0) as number;

        if (userId > 0) {
          const store = useGlobalStore.getState();
          // `store` is a snapshot: selectFacility below replaces the state object, so
          // store.facilityId keeps the value from before the pick. A user whose saved default
          // facility is 0 (DF_Dietech_Facility) then had no facility here, so the role was
          // never fetched - the sidebar sat on its loading rows - and no heartbeat was sent.
          // Always read the facility fresh.
          //
          // Only ever the facility picked from the user's mappings below. It used to fall back to
          // DF_Dietech_Facility, so a user with no mappings (or whose mapping lookup failed) had
          // their role fetched and heartbeat posted at a facility they were never given.
          const currentFacility = () => useGlobalStore.getState().facilityId;
          store.setUserInfo(userId, userName, firstName, lastName, email || '');
          // A new pick may be a different database: never keep the previous one's role.
          store.setUserRole(null, 0);
          // PSI staff: their connection-neutral settings are the same in every connection
          // (hooks/sharedPrefs.ts). Turned on before the stores read them, so this browser's copy
          // applies at once; the server's values follow and are applied when they differ.
          const sharedPrefs = activateSharedPrefs(email, userId, connectionId);
          // Sync theme preferences for this user
          useThemeStore.getState().syncWithUser(userId);
          useSTDisplayStore.getState().syncWithUser(userId);
          if (sharedPrefs) {
            void loadSharedPrefs().then(({ changed }) => {
              if (changed) reapplyUserSettings(useGlobalStore.getState().userId);
            });
          }

          let facilities: Record<string, unknown>[];
          try {
            const facUrl = API.facilityAssociated(defaultFacility, userId);
            const { data: facData } = await apiClient.get(facUrl);
            if (cancelled) return;
            facilities = Array.isArray(facData) ? (facData as Record<string, unknown>[]) : [];
          } catch (facErr) {
            if (cancelled) return;
            // Fail closed. This used to select DF_Dietech_Facility unchecked, opening a facility
            // the user may no longer be mapped to. Without the list there is no facility we know
            // they may use, so stop here and let them try again.
            console.warn('[AuthTokenSetter] Failed to fetch associated facilities:', facErr);
            if (tabFacility > 0) keptFacilityRef.current = { connectionId, facilityId: tabFacility };
            store.clearFacility();
            store.setSessionLoadError('Your facility list could not be loaded.');
            return;
          }

          keptFacilityRef.current = null;
          // The facilities this user last used in THIS connection (never another database's ids).
          const recentIds = getRecentFacilityIds(userId, connectionId);
          // All the rules (tab, default, last used, lowest number) live in the resolver.
          const resolution = resolveFacilityFromMappings(defaultFacility, facilities, tabFacility, recentIds);
          if (resolution.kind === 'select') {
            if (defaultFacility > 0 && resolution.facId !== defaultFacility && resolution.facId !== tabFacility) {
              console.warn(
                `[AuthTokenSetter] User ${userId} DF=${defaultFacility} not in their security mappings — falling back to facility ${resolution.facId}.`
              );
            }
            store.selectFacility(resolution.facId, resolution.facName);
            recordRecentFacility(userId, connectionId, resolution.facId, resolution.facName);
          } else {
            // No facility at all: drop any facility an earlier session left in sessionStorage,
            // and fetch no role and send no heartbeat anywhere.
            store.clearFacility();
            store.setNoFacilityAccess(true);
            console.error(
              `[AuthTokenSetter] User ${userId} has no usable facility mappings (${resolution.reason}).`
            );
            resolvedForRef.current = resolveKey;
            return;
          }

          // Sync feature visibility for this user + facility
          const effectiveFacility = currentFacility();
          if (effectiveFacility > 0) {
            useFeatureVisibilityStore.getState().syncWithUser(userId, effectiveFacility);
          }

          // Fetch user's security role. A user with no role gets NO_ROLE (every level 0). A role
          // that cannot be read stops here with an error and a Try again button: carrying on
          // without it used to leave every permission check open for the whole session.
          const roleId = (get(data, 'fKey_PM_Security_Roles', 'FKey_PM_Security_Roles') ?? 0) as number;
          if (roleId > 0) {
            try {
              const { data: roleData } = await apiClient.get(API.roleById(currentFacility(), roleId));
              if (cancelled) return;
              if (!roleData || typeof roleData !== 'object') throw new Error('The role lookup returned nothing');
              store.setUserRole(roleData, roleId);
            } catch (roleErr) {
              if (cancelled) return;
              console.warn(`[AuthTokenSetter] Failed to fetch role ${roleId}:`, roleErr);
              store.setUserRole(null, 0);
              store.setSessionLoadError('Your security role could not be loaded.');
              return;
            }
          } else {
            store.setUserRole(NO_ROLE, 0);
          }

          // Superseded by a newer pick: that run owns the facility and the
          // heartbeat now, and must not be marked done by this one.
          if (cancelled) return;

          // Send initial heartbeat to mark user as logged in
          const heartbeatFacilityId = currentFacility();
          if (heartbeatFacilityId > 0) {
            try {
              await apiClient.post(API.userHeartbeat(heartbeatFacilityId, userId), {
                computerName: 'React Web',
              });
            } catch (hbErr) {
              console.warn('[AuthTokenSetter] Failed to send initial heartbeat:', hbErr);
            }
          }

          if (cancelled) return;
          resolvedForRef.current = resolveKey;
          console.log(
            `[AuthTokenSetter] Resolved user via /Me: PKey_User=${userId}, DefaultFacility=${defaultFacility}, RoleId=${roleId}`
          );
        } else {
          console.warn('[AuthTokenSetter] /Me returned user but PKey_User is 0', data);
        }
      } catch (err) {
        if (cancelled) return;
        const refusal = ssoRefusalMessage(err);
        if (refusal) {
          // An enterprise sign-in (DSHS, Upstate...) whose AD groups match no security role here.
          console.warn('[AuthTokenSetter] Sign-in refused: no matching security role');
          useGlobalStore.getState().clearFacility();
          useGlobalStore.getState().setSessionLoadRefused(refusal);
          return;
        }
        console.error('[AuthTokenSetter] Failed to resolve user via /Me', err);
        useGlobalStore.getState().setSessionLoadError('Your user account could not be loaded.');
      }
    };
    resolveUser();
    return () => {
      cancelled = true;
    };
  }, [auth.user, selectedConnection, selectionId, retryNonce]);

  // Periodic heartbeat — keep the user marked as logged in while the tab is open.
  // The DB query filters users whose heartbeat is within the last 30 minutes,
  // so we refresh every 10 minutes to stay visible.
  const globalUserId = useGlobalStore((s) => s.userId);
  const globalFacilityId = useGlobalStore((s) => s.facilityId);
  // auth.user keeps its (dead) access token after the sign-in ends, so it
  // cannot tell; the session gate can. Signed out = no heartbeat.
  const pollingAllowed = usePollingAllowed();

  useEffect(() => {
    if (!globalUserId || globalUserId <= 0 || globalFacilityId <= 0 || !auth.user?.access_token || !pollingAllowed)
      return;

    const HEARTBEAT_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes

    // Keeps beating while the tab is hidden: the heartbeat is what keeps the
    // user listed as signed in while the site is open, background tab or not.
    const sendHeartbeat = () => {
      if (isApiBlocked()) {
        clearInterval(interval);
        return;
      }
      apiClient
        .post(API.userHeartbeat(globalFacilityId, globalUserId), {
          computerName: 'React Web',
        })
        .catch((err: unknown) => {
          // Signed out (the renewal failed, or the gate refused it): stop.
          // Otherwise silent fail: the heartbeat is best-effort.
          if (isSignedOutError(err)) clearInterval(interval);
        });
    };

    const interval = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);

    // Clear heartbeat on tab close / navigate away. A keepalive fetch is
    // delivered during page unload like sendBeacon, and can carry the auth
    // and connection headers the endpoint needs.
    const handleUnload = () => {
      // Guard: don't send if IDs haven't been resolved yet
      if (globalUserId <= 0 || globalFacilityId <= 0) return;
      void fetch(apiUrl(API.userHeartbeatClear(globalFacilityId, globalUserId)), {
        method: 'POST',
        keepalive: true,
        headers: { ...apiHeaders(), 'Content-Type': 'application/json' },
        body: '{}',
      }).catch(() => {
        // Best effort: the page is going away.
      });
    };
    window.addEventListener('beforeunload', handleUnload);

    return () => {
      clearInterval(interval);
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, [globalUserId, globalFacilityId, auth.user?.access_token, pollingAllowed]);

  return null;
}
